import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import dateformat from "dateformat";

import {
  AnalyticsCard,
  AnalyticsTable,
  Cell,
  ColumnDef,
  EditFieldsModal,
  LeadsAreaChart,
  TABLE_MIN_HEIGHT,
  TableFooter,
  VisitorProfileCell,
} from "../../common";
import {
  useLeadsTimeSeriesQuery,
  useMetricsQuery,
} from "src/query/experiences/leadsAndReports/reports";
import { useTableState } from "../../hooks/useTableState";
import { selectLead } from "src/redux/actions/experiences/leadsAndReports";
import { convertDatesToTimezone } from "src/services/experiences/leadsAndReports/dateUtils";
import {
  ALWAYS_VISIBLE_UID,
  loadFieldConfig,
  mergeCustomFields,
  saveFieldConfig,
} from "src/services/experiences/leadsAndReports/leadFields";
import { LEAD_REPORT_HEADER } from "../../constants";
import { formatDurationWithLabel } from "../../utils/format";
import {
  LeadFieldConfig,
  LeadReportHeaderField,
} from "src/types/experiences/leadsAndReports";
import {
  selectStoryboard,
  selectStoryboardDraft,
} from "src/redux/selectors/experiences/storyboardSettings";
import { canEdit } from "src/pages/experiences/storyboardSettings/utils/permissions";

import styles from "../../common/common.module.scss";

interface LeadAnalyticsProps {
  storyboardId: string;
  showProfile?: boolean;
  isLeadReportPage?: boolean;
}

const renderFieldData = (lead: any, field: LeadFieldConfig) => {
  if (field.uid == null) {
    const customField = (lead?.fields || []).find(
      (f: any) => f.field === field.fieldId,
    );
    if (!customField) return "-";
    if (typeof customField.values === "boolean") {
      const value = customField.values.toString();
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    }
    if (Array.isArray(customField.values)) {
      return customField.values.length > 0 ? customField.values.join(", ") : "-";
    }
    return customField.values ?? "-";
  }
  switch (field.uid) {
    case "createdDate":
      return dateformat(lead.contact?.[field.uid], "dd mmm yyyy");
    case "lastVisitedDate":
      return dateformat(lead.lastVisitedDate, "dd mmm yyyy");
    case "timeSpent":
      return formatDurationWithLabel(lead[field.uid]).value;
    case "completion":
      return `${lead[field.uid]} %`;
    default:
      if (lead[field.uid] !== undefined) return lead[field.uid] || "-";
      if (lead.contact && lead.contact[field.uid] !== undefined)
        return lead.contact[field.uid] || "-";
      return "";
  }
};

export const LeadAnalytics: React.FC<LeadAnalyticsProps> = ({
  storyboardId,
  showProfile,
  isLeadReportPage,
}) => {
  const dispatch = useDispatch();
  const storyboard = useSelector(selectStoryboard);
  const draft = useSelector(selectStoryboardDraft);
  const canEditFields = canEdit(draft?.role ?? storyboard?.role);
  const { request, key, page, setPage, chart, setChart, enabled, dateFilter } =
    useTableState(storyboardId, isLeadReportPage);
  const { data, isLoading } = useMetricsQuery(
    "leads",
    request,
    key,
    page,
    enabled,
  );
  const rows = data?.records ?? [];

  const { data: timeSeries, isLoading: isGraphLoading } =
    useLeadsTimeSeriesQuery(request, key, enabled && chart === "graph_view");
  const chartData = (
    Array.isArray(timeSeries) ? timeSeries : _.get(timeSeries, "items", [])
  ) as { key: string; value: number }[];

  const header: LeadReportHeaderField[] = useMemo(
    () => mergeCustomFields(LEAD_REPORT_HEADER, rows),
    [rows],
  );

  const [fields, setFields] = useState<LeadFieldConfig[]>(() =>
    loadFieldConfig(storyboardId, LEAD_REPORT_HEADER),
  );
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const next = loadFieldConfig(storyboardId, header);
    setFields((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
    );
  }, [header, storyboardId]);

  const visibleFields = useMemo(
    () => fields.filter((f) => f.visible || f.uid === ALWAYS_VISIBLE_UID),
    [fields],
  );
  const columns: ColumnDef[] = visibleFields.map((f) => ({
    key: f.fieldId,
    header: f.name,
  }));

  const handleDone = (next: LeadFieldConfig[]) => {
    setFields(next);
    saveFieldConfig(storyboardId, next);
    setEditing(false);
  };

  const handleSelect = (row: any) => {
    dispatch(
      selectLead({
        profileId: _.get(row, "contact.id"),
        data: {
          ...row,
          reportDate: convertDatesToTimezone(dateFilter ?? undefined),
        },
      }),
    );
  };

  const editFieldsButton = (
    <button
      className={styles.editFields}
      onClick={() => setEditing(true)}
      disabled={!canEditFields}
    >
      <i className="paperflite-edit" />
      Edit Fields
    </button>
  );

  const renderChart = () => {
    switch (chart) {
      case "graph_view":
        return (
          <div className={styles.chartFull}>
            <LeadsAreaChart
              data={chartData}
              loading={isGraphLoading}
              legend={{ key: "Date", value: "Leads" }}
            />
          </div>
        );
      case "table_view":
        return (
          <>
            <AnalyticsTable
              columns={columns}
              rows={rows}
              loading={isLoading}
              minWidth={columns.length * 160}
              stickyFirstColumn
              renderRow={(row: any) =>
                visibleFields.map((f, ci) =>
                  f.uid === "firstName" ? (
                    <VisitorProfileCell
                      key={ci}
                      data={row}
                      type="leads"
                      showProfile={showProfile}
                      onSelect={handleSelect}
                    />
                  ) : (
                    <Cell key={ci} headline={renderFieldData(row, f)} />
                  ),
                )
              }
            />
            <EditFieldsModal
              show={editing}
              fields={fields}
              onClose={() => setEditing(false)}
              onDone={handleDone}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <AnalyticsCard
      title="Lead Analytics"
      count={data?.totalRecordsCount ?? rows.length}
      action={chart === "table_view" && rows.length > 0 ? editFieldsButton : undefined}
      currentChart={chart}
      disableView={["pie_chart_view"]}
      onChartChange={setChart}
      minHeight={TABLE_MIN_HEIGHT.full}
      footer={
        <TableFooter
          currentChart={chart}
          handlePageChange={setPage}
          pagination={data}
          paginationName="Leads"
        />
      }
    >
      {renderChart()}
    </AnalyticsCard>
  );
};
